import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Maximize, Minimize, RotateCcw, Share2 } from 'lucide-react';
import { Button } from '../ui/button.jsx';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import StoryNarrationController from './StoryNarrationController.js';
import { deriveDisplayPages, getDisplayPageText } from './storyPageLayout.js';
import './Storybook3DReader.css';

const PICTURE_BOOK_DESCRIPTION = 'Illustration-led pages';
const STORYBOOK_DESCRIPTION = 'Text-led pages with companion art';
const TEXT_ONLY_STORY_LABEL = 'TEXT-ONLY STORY';
const DEFAULT_NARRATION = { mode: 'browser' };

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
};

const pageImageUrl = (page) => page?.imageUrl || page?.image_url || page?.image || null;
const getSpreadStartPage = (pageNumber) => (pageNumber > 0 && pageNumber % 2 === 0 ? pageNumber - 1 : pageNumber);

const getSpreadTextures = (pageTextures, blankTexture, pageNumber, singlePage = false) => ({
  // The reader opens as a real two-page spread: 1–2, then 3–4, and so on.
  // Even page numbers can still be active during narration, but they share
  // the same visible spread as the preceding odd page.
  left: singlePage ? blankTexture : (getSpreadStartPage(pageNumber) >= 1 ? pageTextures[getSpreadStartPage(pageNumber) - 1] || blankTexture : blankTexture),
  right: singlePage ? (pageNumber >= 1 ? pageTextures[pageNumber - 1] || blankTexture : blankTexture) : (getSpreadStartPage(pageNumber) >= 1 ? pageTextures[getSpreadStartPage(pageNumber)] || blankTexture : blankTexture),
});

const loadImage = (src, onLoad) => {
  if (!src) return;
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => onLoad(image);
  image.src = src;
};

const drawRoundedRect = (context, x, y, width, height, radius) => {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
};

const createCoverTexture = (title, coverImageUrl, author) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 1600;
  const context = canvas.getContext('2d');
  const draw = (image) => {
    context.fillStyle = '#274d4a';
    context.fillRect(0, 0, canvas.width, canvas.height);
    if (image) {
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.fillStyle = 'rgba(24, 38, 34, 0.42)';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    const lowerShade = context.createLinearGradient(0, 620, 0, canvas.height);
    lowerShade.addColorStop(0, 'rgba(18, 28, 27, 0.08)');
    lowerShade.addColorStop(1, 'rgba(18, 28, 27, 0.48)');
    context.fillStyle = lowerShade;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#ecd19a';
    context.lineWidth = 6;
    context.strokeRect(54, 54, canvas.width - 108, canvas.height - 108);
    context.strokeStyle = 'rgba(236, 209, 154, 0.6)';
    context.lineWidth = 2;
    context.strokeRect(76, 76, canvas.width - 152, canvas.height - 152);
    context.fillStyle = '#f4e5bc';
    context.textAlign = 'center';
    context.font = '800 28px Nunito, sans-serif';
    context.fillText('MAKE IT MINDFUL', canvas.width / 2, 150);
    context.font = '54px Georgia, serif';
    context.fillText('✦', canvas.width / 2, 170);
    context.shadowColor = 'rgba(0, 0, 0, 0.62)';
    context.shadowBlur = 14;
    context.font = '700 78px Georgia, serif';
    const words = String(title || 'Untitled Story').split(/\s+/);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (line && context.measureText(next).width > 860) {
        lines.push(line);
        line = word;
      } else line = next;
    });
    if (line) lines.push(line);
    const startY = 790 - (lines.length - 1) * 48;
    lines.slice(0, 4).forEach((text, index) => context.fillText(text, canvas.width / 2, startY + index * 100));
    context.shadowBlur = 0;
    context.fillRect(canvas.width / 2 - 66, 1260, 132, 4);
    context.font = '800 22px Nunito, sans-serif';
    context.fillText(`BY ${String(author || 'MAKE IT MINDFUL').toUpperCase()}`, canvas.width / 2, 1370);
    context.font = '800 20px Nunito, sans-serif';
    context.fillText('A STORY TO KEEP', canvas.width / 2, 1460);
  };
  draw(null);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  if (coverImageUrl) loadImage(coverImageUrl, (image) => { draw(image); texture.needsUpdate = true; });
  return texture;
};

const createPageTexture = (page, pageNumber, totalPages, readingMode, illustrationsEnabled = true, textScale = 1) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1260;
  canvas.height = 1620;
  const context = canvas.getContext('2d');
  context.scale(1.5, 1.5);
  const logicalWidth = 840;
  const logicalHeight = 1080;
  const background = '#fffaf0';
  const ink = '#241b16';
  const accent = '#624935';
  let pageImage = null;
  let wrappedWords = [];
  let lastActiveWordIndex = -1;

  const wrapText = (text, font, maxWidth) => {
    context.font = font;
    const lines = [];
    let line = '';
    let lineWords = [];
    let wordIndex = 0;
    text.split(/\s+/).filter(Boolean).forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (line && context.measureText(next).width > maxWidth) {
        lines.push({ text: line, words: lineWords });
        line = word;
        lineWords = [{ text: word, index: wordIndex }];
      } else {
        line = next;
        lineWords.push({ text: word, index: wordIndex });
      }
      wordIndex += 1;
    });
    if (line) lines.push({ text: line, words: lineWords });
    return lines;
  };

  const draw = (nextActiveWordIndex = lastActiveWordIndex) => {
    lastActiveWordIndex = nextActiveWordIndex;
    context.fillStyle = background;
    context.fillRect(0, 0, logicalWidth, logicalHeight);
    context.strokeStyle = 'rgba(103, 76, 53, 0.24)';
    context.lineWidth = 3;
    context.strokeRect(30, 30, logicalWidth - 60, logicalHeight - 60);

    if (pageNumber === 0) {
      wrappedWords = [];
      return;
    }

    const isStorybook = readingMode === 'storybook';
    const showIllustration = illustrationsEnabled && Boolean(pageImage);
    // Missing art should not create a misleading empty panel. The page can
    // still be read as a text-first page while an optional asset is retried.
    const showIllustrationSlot = showIllustration;
    const image = isStorybook
      ? { x: 255, y: 62, width: 330, height: 225 }
      : { x: 68, y: 62, width: 704, height: 455 };
    const textTop = showIllustrationSlot ? (isStorybook ? 335 : 590) : 150;
    const textBottom = 960;
    const textWidth = isStorybook ? 680 : 704;
    const baseFontSize = Math.round((isStorybook ? 50 : 31) * textScale);
    const minFontSize = Math.round((isStorybook ? 32 : 23) * textScale);
    let fontSize = baseFontSize;
    let lineHeight = isStorybook ? 47 : 43;
    let lines = [];
    const text = getDisplayPageText(page) || 'Your story is still taking shape.';
    while (fontSize >= minFontSize) {
      lines = wrapText(text, `600 ${fontSize}px Georgia, serif`, textWidth);
      lineHeight = Math.round(fontSize * 1.38);
      if (lines.length * lineHeight <= textBottom - textTop) break;
      fontSize -= 1;
    }
    wrappedWords = lines.flatMap((lineData) => lineData.words);

    if (showIllustrationSlot) {
      drawRoundedRect(context, image.x, image.y, image.width, image.height, 14);
      context.save();
      context.clip();
      const scale = Math.max(image.width / pageImage.width, image.height / pageImage.height);
      const width = pageImage.width * scale;
      const height = pageImage.height * scale;
      context.drawImage(pageImage, image.x + (image.width - width) / 2, image.y + (image.height - height) / 2, width, height);
      context.restore();
    }

    context.fillStyle = accent;
    context.font = '800 13px Nunito, sans-serif';
    context.textAlign = 'center';
    context.fillText(`PAGE ${pageNumber} / ${totalPages}`, logicalWidth / 2, showIllustrationSlot ? (isStorybook ? 382 : 555) : 92);

    context.font = `600 ${fontSize}px Georgia, serif`;
    context.textAlign = 'left';
    const totalTextHeight = lines.length * lineHeight;
    const startY = textTop + Math.max(0, (textBottom - textTop - totalTextHeight) / 2) + fontSize;
    lines.forEach((lineData, lineIndex) => {
      let x = isStorybook ? 80 : (logicalWidth - context.measureText(lineData.text).width) / 2;
      const y = startY + lineIndex * lineHeight;
      lineData.words.forEach((wordData) => {
        const wordWidth = context.measureText(wordData.text).width;
        if (wordData.index === lastActiveWordIndex) {
          context.fillStyle = '#f3c94f';
          drawRoundedRect(context, x - 5, y - fontSize, wordWidth + 10, fontSize + 9, 6);
        }
        context.fillStyle = ink;
        context.fillText(wordData.text, x, y);
        x += wordWidth + context.measureText(' ').width;
      });
    });

    context.fillStyle = 'rgba(45, 33, 25, 0.62)';
    context.font = '800 11px Nunito, sans-serif';
    context.textAlign = 'center';
    context.fillText('MAKE IT MINDFUL', logicalWidth / 2, 1020);
  };
  draw();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const imageUrl = pageImageUrl(page);
  if (imageUrl) loadImage(imageUrl, (image) => { pageImage = image; draw(lastActiveWordIndex); texture.needsUpdate = true; });
  return {
    texture,
    redraw: (nextActiveWordIndex = -1) => {
      draw(nextActiveWordIndex);
      texture.needsUpdate = true;
    },
    getWordCount: () => wrappedWords.length,
  };
};

function Storybook3DReader({
  title,
  author = 'Make It Mindful',
  pages = [],
  storyId = null,
  coverImageUrl = null,
  initialPage = 0,
  readingMode = 'picture',
  onReadingModeChange = null,
  onPageChange = null,
  showControls = true,
  audioPlayer = null,
  isGeneratingImages = false,
  illustrationsEnabled = null,
  narration = DEFAULT_NARRATION,
  defaultFocusMode = false,
  onShare = null,
}) {
  const canvasRef = useRef(null);
  const readerRef = useRef(null);
  const stageRef = useRef(null);
  const runtimeRef = useRef(null);
  const modeStorageKey = `make-it-mindful:reader-mode:${storyId || title || 'story'}`;
  const [activeReadingMode, setActiveReadingMode] = useState(() => {
    try {
      const saved = window.localStorage.getItem(modeStorageKey);
      return saved === 'storybook' || saved === 'picture' ? saved : readingMode;
    } catch {
      return readingMode;
    }
  });
  const displayPages = useMemo(() => deriveDisplayPages(pages, activeReadingMode), [activeReadingMode, pages]);
  const [currentPage, setCurrentPage] = useState(Math.min(initialPage, displayPages.length));
  const [isFullscreen, setIsFullscreen] = useState(defaultFocusMode);
  const [textScale, setTextScale] = useState(1);
  const [isTurning, setIsTurning] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const totalPages = displayPages.length + 1;
  const singlePageMode = activeReadingMode === 'storybook';
  const isCover = currentPage === 0;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const spreadStartPage = getSpreadStartPage(currentPage);
  const spreadEndPage = singlePageMode ? currentPage : Math.min(spreadStartPage + 1, displayPages.length);
  const canGoNext = isCover ? displayPages.length > 0 : spreadEndPage < displayPages.length;
  const canGoPrevious = currentPage > 0;
  const narrationRef = useRef(null);
  const turnDirectionRef = useRef(1);
  const pendingNarrationPageRef = useRef(null);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const hasIllustrations = illustrationsEnabled == null
    ? displayPages.some((page) => Boolean(pageImageUrl(page))) || isGeneratingImages
    : illustrationsEnabled;

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleReadingModeChange = useCallback((_, nextMode) => {
    if (!nextMode || nextMode === activeReadingMode) return;
    narrationRef.current?.stop?.();
    setActiveReadingMode(nextMode);
    const nextDisplayPageCount = deriveDisplayPages(pages, nextMode).length;
    setCurrentPage((page) => page === 0 ? 0 : Math.min(page, nextDisplayPageCount));
    setActiveWordIndex(-1);
    try { window.localStorage.setItem(modeStorageKey, nextMode); } catch { /* storage is optional */ }
    onReadingModeChange?.(nextMode);
  }, [activeReadingMode, modeStorageKey, onReadingModeChange]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, displayPages.length));
  }, [displayPages.length]);

  const goToPage = useCallback((nextPage, { fromNarration = false } = {}) => {
    const clamped = Math.max(0, Math.min(nextPage, totalPages - 1));
    if (clamped === currentPage) return;
    if (isTurning) {
      if (fromNarration) pendingNarrationPageRef.current = clamped;
      return;
    }
    turnDirectionRef.current = clamped > currentPage ? 1 : -1;
    if (!fromNarration) narrationRef.current?.stop?.();
    setIsTurning(true);
    setCurrentPage(clamped);
    onPageChange?.(clamped);
    window.setTimeout(() => setIsTurning(false), prefersReducedMotion ? 0 : 720);
  }, [currentPage, isTurning, onPageChange, prefersReducedMotion, totalPages]);

  useEffect(() => {
    if (isTurning || pendingNarrationPageRef.current == null) return;
    const pendingPage = pendingNarrationPageRef.current;
    pendingNarrationPageRef.current = null;
    goToPage(pendingPage, { fromNarration: true });
  }, [goToPage, isTurning]);

  const nextPage = useCallback(() => {
    if (!canGoNext) return;
    if (currentPage === 0) return goToPage(1);
    return goToPage(Math.min(singlePageMode ? currentPage + 1 : (currentPage % 2 === 1 ? currentPage + 2 : currentPage + 1), displayPages.length));
  }, [canGoNext, currentPage, displayPages.length, goToPage, singlePageMode]);
  const nextPageRef = useRef(nextPage);
  nextPageRef.current = nextPage;
  const previousPage = useCallback(() => {
    if (!canGoPrevious) return;
    if (currentPage <= 1 || singlePageMode) return goToPage(currentPage - 1);
    return goToPage(currentPage % 2 === 1 ? currentPage - 2 : currentPage - 1);
  }, [canGoPrevious, currentPage, goToPage, singlePageMode]);
  const handlePageComplete = useCallback((nextPageNumber) => {
    goToPage(nextPageNumber, { fromNarration: true });
  }, [goToPage]);


  useEffect(() => {
    const handleKeyDown = (event) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      if (event.key === 'ArrowRight' || event.key === ' ') { event.preventDefault(); nextPage(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); previousPage(); }
      if (event.key === 'Home') { event.preventDefault(); goToPage(0); }
      if (event.key === 'End') { event.preventDefault(); goToPage(totalPages - 1); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPage, nextPage, previousPage, totalPages]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = stageRef.current;
    if (!canvas || !frame) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch {
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#d9d0c3');
    const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
    camera.position.set(0, 3.25, 8.6);
    camera.lookAt(0, 0, 0);
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const environmentTarget = pmremGenerator.fromScene(new RoomEnvironment(), 0.04);
    scene.environment = environmentTarget.texture;
    scene.environmentIntensity = 0.75;
    pmremGenerator.dispose();
    scene.add(new THREE.HemisphereLight('#fff6df', '#6d5b4d', 2.3));
    const keyLight = new THREE.DirectionalLight('#fff2d2', 3.4);
    keyLight.position.set(-4, 8, 7);
    keyLight.castShadow = true;
    scene.add(keyLight);
    const fillLight = new THREE.PointLight('#c98d68', 2.1, 15);
    fillLight.position.set(4, 2, 5);
    scene.add(fillLight);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 30),
      new THREE.MeshStandardMaterial({ color: '#cbbdad', roughness: 0.96 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2.1;
    floor.receiveShadow = true;
    scene.add(floor);

    const assembly = new THREE.Group();
    scene.add(assembly);
    const book = new THREE.Group();
    book.rotation.x = -0.08;
    assembly.add(book);
    const pageWidth = 2.72;
    const pageHeight = 3.72;
    const pageDepth = 0.07;
    const pageTextureRecords = displayPages.map((page, index) => createPageTexture(page, index + 1, displayPages.length, activeReadingMode, hasIllustrations, textScale));
    const blankRecord = createPageTexture({ text: '' }, 0, displayPages.length, activeReadingMode, hasIllustrations, textScale);
    const pageTextures = pageTextureRecords.map((record) => record.texture);
    const blankTexture = blankRecord.texture;
    // Page art contains typography; keep it unlit so environmental lighting
    // cannot wash the words out.
    const pageMaterial = (texture) => new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false });
    // BoxGeometry is width (x), height (y), depth (z). Keep the page upright
    // and use the shallow z-axis only for the physical paper thickness.
    const leftPage = new THREE.Mesh(new THREE.BoxGeometry(pageWidth, pageHeight, pageDepth), pageMaterial(blankTexture));
    const rightPage = new THREE.Mesh(new THREE.BoxGeometry(pageWidth, pageHeight, pageDepth), pageMaterial(pageTextures[0] || blankTexture));
    leftPage.position.x = -pageWidth / 2;
    rightPage.position.x = pageWidth / 2;
    leftPage.castShadow = leftPage.receiveShadow = rightPage.castShadow = rightPage.receiveShadow = true;
    book.add(leftPage, rightPage);

    const coverTexture = createCoverTexture(title, coverImageUrl, author);
    const coverMaterial = new THREE.MeshStandardMaterial({ color: '#31544d', roughness: 0.74, metalness: 0.08 });
    const coverArtMaterial = new THREE.MeshBasicMaterial({ map: coverTexture });
    const openLeftCover = new THREE.Mesh(new THREE.BoxGeometry(pageWidth + 0.18, pageHeight + 0.18, 0.13), coverMaterial);
    const openRightCover = openLeftCover.clone();
    openLeftCover.position.x = -pageWidth / 2;
    openRightCover.position.x = pageWidth / 2;
    openLeftCover.position.y = -0.1;
    openRightCover.position.y = -0.1;
    // Keep the boards behind the page faces so the illustrated paper remains visible.
    openLeftCover.position.z = -0.22;
    openRightCover.position.z = -0.22;
    book.add(openLeftCover, openRightCover);
    const coverArt = new THREE.Mesh(new THREE.PlaneGeometry(2.56, 3.48), coverArtMaterial);
    coverArt.position.set(0, 0.09, 0.3);
    coverArt.visible = false;
    book.add(coverArt);

    const closedBook = new THREE.Group();
    const closedBoard = new THREE.Mesh(new THREE.BoxGeometry(pageWidth + 0.36, pageHeight + 0.18, 0.22), coverMaterial.clone());
    const closedArt = new THREE.Mesh(new THREE.PlaneGeometry(pageWidth - 0.05, pageHeight - 0.05), coverArtMaterial);
    closedArt.position.z = 0.14;
    closedBook.add(closedBoard, closedArt);
    closedBook.position.y = 0.12;
    closedBook.rotation.x = -0.03;
    assembly.add(closedBook);

    // The animated leaf is pivoted at the spine. Rotating a centered page
    // makes the spine appear to move; this pivot keeps the hinge fixed while
    // the outside edge travels across the book.
    const turnPivot = new THREE.Group();
    const turnPage = new THREE.Mesh(new THREE.BoxGeometry(pageWidth, pageHeight, pageDepth * 0.9), pageMaterial(pageTextures[0] || blankTexture));
    turnPage.position.set(pageWidth / 2, 0, 0.08);
    turnPage.renderOrder = 10;
    turnPage.material.depthTest = false;
    turnPage.material.depthWrite = false;
    turnPivot.add(turnPage);
    book.add(turnPivot);
    turnPage.visible = false;

    const applyAssemblyScale = (pageNumber = currentPage) => {
      const spreadStart = getSpreadStartPage(pageNumber);
      const hasSinglePage = pageNumber > 0 && spreadStart >= 1 && spreadStart === displayPages.length;
      const singlePageBoost = singlePageMode ? 1.35 : (hasSinglePage ? 1.2 : 1);
      assembly.scale.setScalar(Math.min(1, Math.max(0.72, (frame.clientWidth / 520) * singlePageBoost)));
    };

    const updateSpread = (pageNumber) => {
      const { left, right } = getSpreadTextures(pageTextures, blankTexture, pageNumber, singlePageMode);
      leftPage.material.map = left;
      rightPage.material.map = right;
      const spreadStart = getSpreadStartPage(pageNumber);
      const hasLeftPage = !singlePageMode && spreadStart >= 1 && Boolean(pageTextures[spreadStart - 1]);
      const hasRightPage = singlePageMode
        ? pageNumber >= 1 && Boolean(pageTextures[pageNumber - 1])
        : spreadStart >= 1 && Boolean(pageTextures[spreadStart]);
      leftPage.visible = hasLeftPage;
      rightPage.visible = hasRightPage;
      openLeftCover.visible = hasLeftPage;
      openRightCover.visible = hasRightPage;
      book.position.x = singlePageMode ? -pageWidth / 2 : (hasLeftPage && !hasRightPage ? pageWidth / 2 : 0);
      leftPage.material.needsUpdate = true;
      rightPage.material.needsUpdate = true;
      const coverOpen = pageNumber > 0;
      book.visible = coverOpen;
      closedBook.visible = !coverOpen;
      coverArt.visible = false;
      applyAssemblyScale(pageNumber);
    };
    updateSpread(currentPage);
    runtimeRef.current = { scene, camera, renderer, assembly, book, closedBook, turnPivot, turnPage, leftPage, rightPage, openLeftCover, openRightCover, pageWidth, pageTextures, pageTextureRecords, blankTexture, blankRecord, applyAssemblyScale };

    const resize = () => {
      renderer.setSize(frame.clientWidth, frame.clientHeight, false);
      camera.aspect = frame.clientWidth / frame.clientHeight;
      camera.updateProjectionMatrix();
      // Keep a two-page spread inside narrow phone viewports without changing
      // the desktop scale or the book's stable camera model.
      runtimeRef.current?.applyAssemblyScale?.();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(frame);
    resize();

    let pointerStart = null;
    let pointerMoved = false;
    let isDragging = false;
    let targetRotationY = 0;
    let targetRotationX = 0;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const handlePointerDown = (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
      pointerMoved = false;
      isDragging = true;
      canvas.setPointerCapture?.(event.pointerId);
    };
    const handlePointerMove = (event) => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) pointerMoved = true;
      if (pointerMoved) {
        targetRotationY = clamp(dx * 0.0022, -0.14, 0.14);
        targetRotationX = clamp(-0.08 - dy * 0.0016, -0.15, 0.02);
      }
    };
    const handlePointerUp = (event) => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      isDragging = false;
      canvas.releasePointerCapture?.(event.pointerId);
      if (currentPageRef.current === 0 && !pointerMoved) {
        nextPageRef.current();
      } else if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) nextPage();
        else previousPage();
      }
      targetRotationY = 0;
      targetRotationX = 0;
    };
    const handlePointerCancel = () => {
      pointerStart = null;
      isDragging = false;
      targetRotationY = 0;
      targetRotationX = 0;
    };
    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handlePointerCancel);

    let animationFrame;
    const animate = () => {
      if (!isDragging) {
        assembly.rotation.y += (targetRotationY - assembly.rotation.y) * 0.14;
        assembly.rotation.x += (targetRotationX - assembly.rotation.x) * 0.14;
      } else {
        assembly.rotation.y += (targetRotationY - assembly.rotation.y) * 0.24;
        assembly.rotation.x += (targetRotationX - assembly.rotation.x) * 0.24;
      }
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handlePointerCancel);
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => { material.map?.dispose(); material.dispose(); });
        }
      });
      environmentTarget.texture.dispose();
      renderer.dispose();
      runtimeRef.current = null;
    };
  }, [activeReadingMode, author, coverImageUrl, displayPages, hasIllustrations, singlePageMode, textScale, title]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return undefined;
    runtime.closedBook.visible = currentPage === 0;
    runtime.book.visible = currentPage > 0;
    const { left, right } = getSpreadTextures(runtime.pageTextures, runtime.blankTexture, currentPage, singlePageMode);
    runtime.leftPage.material.map = left;
    runtime.rightPage.material.map = right;
    const hasLeftPage = !singlePageMode && spreadStartPage >= 1 && Boolean(runtime.pageTextures[spreadStartPage - 1]);
    const hasRightPage = singlePageMode
      ? currentPage >= 1 && Boolean(runtime.pageTextures[currentPage - 1])
      : spreadStartPage >= 1 && Boolean(runtime.pageTextures[spreadStartPage]);
    runtime.leftPage.visible = hasLeftPage;
    runtime.rightPage.visible = hasRightPage;
    runtime.openLeftCover.visible = hasLeftPage;
    runtime.openRightCover.visible = hasRightPage;
    runtime.book.position.x = singlePageMode ? -runtime.pageWidth / 2 : (hasLeftPage && !hasRightPage ? runtime.pageWidth / 2 : 0);
    runtime.leftPage.material.needsUpdate = true;
    runtime.rightPage.material.needsUpdate = true;
    const visiblePageNumbers = singlePageMode
      ? [currentPage]
      : [spreadStartPage, spreadStartPage + 1];
    visiblePageNumbers.forEach((pageNumber) => {
      if (pageNumber < 1 || pageNumber > displayPages.length) return;
      runtime.pageTextureRecords[pageNumber - 1]?.redraw(pageNumber === currentPage ? activeWordIndex : -1);
    });
    if (currentPage === 0) runtime.blankRecord.redraw(-1);

    const direction = turnDirectionRef.current;
    const shouldAnimateLeaf = direction > 0 && (singlePageMode ? currentPage > 1 : currentPage > 1 && currentPage % 2 === 1);
    const turningPivot = runtime.turnPivot;
    const outgoingTexture = runtime.pageTextures[(singlePageMode ? currentPage : spreadStartPage) - 2] || runtime.blankTexture;
    turningPivot.rotation.y = 0;
    runtime.turnPage.position.x = direction > 0 ? 1.36 : -1.36;
    runtime.turnPage.material.map = outgoingTexture;
    runtime.turnPage.material.needsUpdate = true;
    runtime.turnPage.visible = shouldAnimateLeaf;
    if (shouldAnimateLeaf) {
      // The outgoing leaf is represented by the pivoted page while it moves;
      // leave the destination leaf underneath it until the turn completes.
      runtime.leftPage.material.map = runtime.blankTexture;
      runtime.leftPage.material.needsUpdate = true;
    }
    const start = performance.now();
    const duration = prefersReducedMotion ? 0 : 680;
    let frame;
    const animateTurn = (time) => {
      const progress = duration ? Math.min(1, (time - start) / duration) : 1;
      const eased = 1 - Math.pow(1 - progress, 3);
      // Swing the leaf toward the viewer while it crosses the spine. The
      // negative turn keeps the outer edge in front of the static spread.
      if (shouldAnimateLeaf) turningPivot.rotation.y = -eased * Math.PI;
      if (progress < 1) frame = requestAnimationFrame(animateTurn);
      else {
        turningPivot.rotation.y = 0;
        runtime.turnPage.visible = false;
        const finalSpread = getSpreadTextures(runtime.pageTextures, runtime.blankTexture, currentPage, singlePageMode);
        runtime.leftPage.material.map = finalSpread.left;
        runtime.rightPage.material.map = finalSpread.right;
        runtime.leftPage.material.needsUpdate = true;
        runtime.rightPage.material.needsUpdate = true;
      }
    };
    frame = requestAnimationFrame(animateTurn);
    return () => cancelAnimationFrame(frame);
  }, [currentPage, displayPages.length, prefersReducedMotion, singlePageMode, spreadStartPage]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || isCover) return undefined;
    const visiblePageNumbers = singlePageMode
      ? [currentPage]
      : [spreadStartPage, spreadStartPage + 1];
    visiblePageNumbers.forEach((pageNumber) => {
      if (pageNumber < 1 || pageNumber > displayPages.length) return;
      runtime.pageTextureRecords[pageNumber - 1]?.redraw(pageNumber === currentPage ? activeWordIndex : -1);
    });
    return undefined;
  }, [activeWordIndex, currentPage, displayPages.length, isCover, singlePageMode, spreadStartPage]);

  const openBook = useCallback(() => {
    if (currentPage === 0) nextPage();
  }, [currentPage, nextPage]);

  const toggleFullscreen = async () => {
    if (isFullscreen) {
      if (document.fullscreenElement) await document.exitFullscreen?.();
      setIsFullscreen(false);
      return;
    }
    try { await stageRef.current?.requestFullscreen?.(); } catch { /* CSS book fullscreen still works when native fullscreen is unavailable. */ }
    setIsFullscreen(true);
  };

  const currentDescription = useMemo(() => {
    if (isCover) return 'Cover';
    return spreadEndPage > spreadStartPage
      ? `Pages ${spreadStartPage}–${spreadEndPage} of ${displayPages.length}`
      : `Page ${spreadStartPage} of ${displayPages.length}`;
  }, [displayPages.length, isCover, spreadEndPage, spreadStartPage]);
  const modeDescription = activeReadingMode === 'storybook'
    ? STORYBOOK_DESCRIPTION
    : (!hasIllustrations ? `${TEXT_ONLY_STORY_LABEL} · Illustration-free pages` : PICTURE_BOOK_DESCRIPTION);
  const pageDots = (
    <div className="storybook-3d-reader__dots" role="group" aria-label="Story pages">
      {[0, ...Array.from({ length: singlePageMode ? displayPages.length : Math.ceil(displayPages.length / 2) }, (_, index) => singlePageMode ? index + 1 : 1 + index * 2)].map((index) => {
        const isActive = index === 0 ? currentPage === 0 : singlePageMode ? currentPage === index : currentPage >= index && currentPage <= index + 1;
        const label = index === 0 ? 'Go to cover' : singlePageMode ? `Go to page ${index}` : index + 1 <= displayPages.length ? `Go to pages ${index} and ${index + 1}` : `Go to page ${index}`;
        return <button type="button" key={index} className={isActive ? 'is-active' : ''} onClick={() => goToPage(index)} aria-label={label} aria-current={isActive ? 'page' : undefined} />;
      })}
    </div>
  );
  return (
    <div ref={readerRef} className={`storybook-3d-reader ${isFullscreen ? 'is-fullscreen' : ''}`}>
      {showControls && (
        <div className="storybook-3d-reader__toolbar">
          <div className="storybook-3d-reader__mode-toggle" role="group" aria-label="Reading view">
            <Button type="button" size="sm" variant={activeReadingMode === 'picture' ? 'secondary' : 'ghost'} aria-label="Picture Book view" aria-pressed={activeReadingMode === 'picture'} onClick={() => handleReadingModeChange(null, 'picture')}>Picture Book</Button>
            <Button type="button" size="sm" variant={activeReadingMode === 'storybook' ? 'secondary' : 'ghost'} aria-label="Storybook view" aria-pressed={activeReadingMode === 'storybook'} onClick={() => handleReadingModeChange(null, 'storybook')}>Storybook</Button>
          </div>
          <div className="storybook-3d-reader__status">
            <span>{currentDescription}</span>
            <small>{isCover ? 'Tap the book or choose Open book' : modeDescription}</small>
          </div>
          <div className="storybook-3d-reader__text-size" role="group" aria-label="Text size">
            {[['small', 0.9, 'A−'], ['standard', 1, 'A'], ['large', 1.2, 'A+']].map(([label, scale, text]) => (
              <button type="button" key={label} className={textScale === scale ? 'is-active' : ''} onClick={() => setTextScale(scale)} aria-label={`${label} text`} aria-pressed={textScale === scale}>{text}</button>
            ))}
          </div>
          {onShare && <Button className="storybook-3d-reader__share" variant="ghost" size="icon" onClick={onShare} aria-label="Share story">
            <Share2 size={18} aria-hidden="true" />
          </Button>}
          <Button className="storybook-3d-reader__fullscreen-toggle" variant="ghost" size="icon" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
            {isFullscreen ? <Minimize size={18} aria-hidden="true" /> : <Maximize size={18} aria-hidden="true" />}
          </Button>
        </div>
      )}
      <div className={`storybook-3d-reader__stage ${isCover ? 'is-cover' : ''} ${isFullscreen ? 'is-book-fullscreen' : ''}`} ref={stageRef}>
        <canvas ref={canvasRef} aria-label={`${title} 3D reader`} />
        {isFullscreen && <Button variant="ghost" size="icon" className="storybook-3d-reader__book-fullscreen-exit" onClick={toggleFullscreen} aria-label="Exit book fullscreen">
          <Minimize size={18} aria-hidden="true" />
        </Button>}
        {!isCover && <Button variant="ghost" size="icon" className="storybook-3d-reader__nav is-left" onClick={previousPage} disabled={!canGoPrevious || isTurning} aria-label="Previous page"><ArrowLeft size={20} aria-hidden="true" /></Button>}
        <Button variant="ghost" size="icon" className="storybook-3d-reader__nav is-right" onClick={nextPage} disabled={!canGoNext || isTurning} aria-label="Next page"><ArrowRight size={20} aria-hidden="true" /></Button>
        {isGeneratingImages && <span className="storybook-3d-reader__generating">Illustrations are still being painted</span>}
      </div>
      {isCover ? <div className="storybook-3d-reader__caption is-cover-caption" aria-live="polite">
        <p className="storybook-3d-reader__caption-kicker">Cover</p>
        <p>
          {`Open “${title || 'Untitled Story'}” to begin reading.`}
        </p>
        <Button
            className="storybook-3d-reader__open-button"
            onClick={openBook}
        >
          Open book <ArrowRight size={17} aria-hidden="true" />
        </Button>
        {pageDots}
      </div> : <div className="storybook-3d-reader__page-nav">{pageDots}</div>}
      <StoryNarrationController
        ref={narrationRef}
        {...narration}
        pages={displayPages}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageComplete={handlePageComplete}
        onWordChange={setActiveWordIndex}
      />
      {audioPlayer && <div className="storybook-3d-reader__audio">{audioPlayer}</div>}
      <div className="storybook-3d-reader__tip"><RotateCcw size={13} aria-hidden="true" /> Drag to tilt the book · swipe or use the arrows to turn pages</div>
    </div>
  );
}

export default Storybook3DReader;
